import { Check, ChevronDown, ChevronRight, ClipboardCheck, Loader2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { QueryErrorCard } from "@/components/query-error-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  apiErrorMessage,
  type CustomerPendingOrder,
  useApproveOrder,
  useMe,
  usePendingOrders,
  useRejectOrder,
} from "@/lib/api";
import { cn } from "@/lib/utils";

import { RejectOrderDialog } from "./reject-order-dialog";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

// Money fields are nullable in the contract; render an em dash rather than a
// misleading "$NaN"/"$0.00" when a value is missing.
function formatUsd(value: number | null | undefined): string {
  return value == null ? "—" : currency.format(value);
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export function PortalApprovalsPage() {
  const { data: me } = useMe();
  const ordersQuery = usePendingOrders();
  const orders = ordersQuery.data ?? [];

  const approveOrder = useApproveOrder();
  const rejectOrder = useRejectOrder();

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<CustomerPendingOrder | null>(null);

  const toggleExpanded = (orderId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleApprove = async (order: CustomerPendingOrder) => {
    setPendingId(order.order_id);
    try {
      await approveOrder.mutateAsync({ orderId: order.order_id });
      toast.success(`Approved order ${order.sales_order_number}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't approve this order. Please try again."));
    } finally {
      setPendingId(null);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejecting) {
      return;
    }
    const order = rejecting;
    setPendingId(order.order_id);
    try {
      await rejectOrder.mutateAsync({ orderId: order.order_id, reason });
      toast.success(`Rejected order ${order.sales_order_number}`);
      setRejecting(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't reject this order. Please try again."));
    } finally {
      setPendingId(null);
    }
  };

  // Direct URL access without the capability: show a friendly no-access state.
  if (me && !me.capabilities?.approve_orders) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <PageHeader eyebrow="Approvals" title="No approval access" />
        <div className="bg-card border-border text-muted rounded-lg border p-5 text-sm leading-relaxed shadow-sm">
          You don&apos;t have permission to approve orders. Contact your J&amp;J representative if
          you need this enabled.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow="Approvals"
        title="Pending order approvals"
        description="Review orders placed by your colleagues that need sign-off before J&J fulfills them."
      />

      {ordersQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      ) : ordersQuery.isError ? (
        <QueryErrorCard
          message="Couldn't load pending orders."
          onRetry={() => void ordersQuery.refetch()}
        />
      ) : orders.length === 0 ? (
        <div className="bg-card border-border flex flex-col items-center justify-center gap-2 rounded-lg border px-6 py-16 text-center shadow-sm">
          <ClipboardCheck className="text-muted h-7 w-7" />
          <p className="text-foreground text-sm font-medium">All caught up</p>
          <p className="text-muted max-w-xs text-sm">
            There are no orders waiting for your approval right now.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const isExpanded = expanded.has(order.order_id);
            const isBusy = pendingId === order.order_id;
            return (
              <div
                key={order.order_id}
                className="bg-card border-border overflow-hidden rounded-lg border shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(order.order_id)}
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    aria-expanded={isExpanded}
                  >
                    <span className="text-muted mt-0.5 shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className="text-foreground flex flex-wrap items-center gap-2 text-sm font-medium">
                        {order.sales_order_number}
                        {order.po_number && (
                          <span className="text-muted font-normal">PO {order.po_number}</span>
                        )}
                      </span>
                      <span className="text-muted mt-1 block text-xs">
                        {formatDate(order.order_date)}
                        {order.placed_by_name && ` · ${order.placed_by_name}`}
                        {order.placed_by_email && ` (${order.placed_by_email})`} ·{" "}
                        {order.lines.length} {order.lines.length === 1 ? "line" : "lines"}
                      </span>
                    </span>
                  </button>

                  <div className="flex items-center gap-3">
                    <span className="text-foreground text-sm font-semibold">
                      {formatUsd(order.total_value_usd)}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={isBusy} onClick={() => void handleApprove(order)}>
                        {isBusy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setRejecting(order)}
                      >
                        <X className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-border border-t">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-warm-gray-01 text-muted">
                        <tr>
                          <th className="px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase">
                            SKU
                          </th>
                          <th className="px-4 py-2 text-[10px] font-medium tracking-[0.16em] uppercase">
                            Description
                          </th>
                          <th className="px-4 py-2 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                            Qty
                          </th>
                          <th className="px-4 py-2 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                            Unit price
                          </th>
                          <th className="px-4 py-2 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                            Line total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-border divide-y">
                        {order.lines.map((line) => (
                          <tr key={line.sku} className={cn("text-foreground")}>
                            <td className="px-4 py-2 font-mono text-xs">{line.sku}</td>
                            <td className="px-4 py-2">{line.description ?? "—"}</td>
                            <td className="px-4 py-2 text-right">{line.quantity}</td>
                            <td className="text-muted px-4 py-2 text-right">
                              {formatUsd(line.unit_price_usd)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium">
                              {formatUsd(
                                line.unit_price_usd == null
                                  ? null
                                  : line.unit_price_usd * line.quantity,
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <RejectOrderDialog
        open={!!rejecting}
        onOpenChange={(open) => {
          if (!open) {
            setRejecting(null);
          }
        }}
        orderLabel={rejecting?.sales_order_number ?? ""}
        isPending={!!rejecting && pendingId === rejecting.order_id}
        onConfirm={handleReject}
      />
    </div>
  );
}
